import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  LineChart,
  Lightbulb,
  LogOut,
  Package as PackageIcon,
  PawPrint,
  ShoppingBag,
  Sparkles,
  TrendingDown,
} from 'lucide-react'

import { ApiError } from './api/types'
import { api, auth, billing } from './api/client'
import type {
  ChannelAttributionResult,
  LostRevenueResult,
  MarketingSegmentsResult,
  OccupancyResult,
  RevenueResult,
  SegmentPreviewResult,
} from './api/types'
import Assistant from './Assistant'
import BookingPagePanel from './BookingPagePanel'
import PoliciesPanel from './PoliciesPanel'
import SubscriptionBanner from './components/SubscriptionBanner'
import { ChannelDonut, OccupancyHeatStrip, ServiceMixBars, revenueRows } from './Charts'
import { MarketingSection, SendWinbackDialog } from './Marketing'
import { Badge } from './components/ui/badge'
import { Button } from './components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card'
import { toast } from './components/ui/use-toast'

type RangeKey = 'last7' | 'last30' | 'last90'
const RANGES: Record<RangeKey, number> = { last7: 7, last30: 30, last90: 90 }

const isoDaysAgo = (n: number) => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}
const todayISO = () => new Date().toISOString().slice(0, 10)

const fmtMoney = (n: number, locale = 'zh-Hant-TW') =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'TWD',
    maximumFractionDigits: 0,
  }).format(n)

const fmtPct = (n: number) => `${Math.round(n * 100)}%`

export default function Cockpit() {
  const { t, i18n } = useTranslation()
  const nav = useNavigate()
  const [rangeKey, setRangeKey] = useState<RangeKey>('last30')
  const [occ, setOcc] = useState<OccupancyResult | null>(null)
  const [rev, setRev] = useState<RevenueResult | null>(null)
  const [chan, setChan] = useState<ChannelAttributionResult | null>(null)
  const [lost, setLost] = useState<LostRevenueResult | null>(null)
  const [seg, setSeg] = useState<MarketingSegmentsResult | null>(null)
  const [pkgs, setPkgs] = useState<{
    sold_count: number
    sold_revenue_minor: number
    expiring_within_7d: number
  } | null>(null)
  const [winbackOpen, setWinbackOpen] = useState(false)
  const [winbackPreview, setWinbackPreview] = useState<SegmentPreviewResult | null>(null)
  const [winbackBusy, setWinbackBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  const isMock = auth.isMock()
  const range = useMemo(() => {
    const days = RANGES[rangeKey] - 1
    return { start_date: isoDaysAgo(days), end_date: todayISO() }
  }, [rangeKey])

  useEffect(() => {
    if (!isMock && !auth.token) {
      nav('/login', { replace: true })
      return
    }
    let alive = true
    setLoading(true)
    setErr(null)
    Promise.all([
      api.occupancy(range),
      api.revenue(range),
      api.channelAttribution(range),
      api.lostRevenue(range),
      api.marketingSegments(),
      billing
        .getPackagesPeriodSummary({
          from_date: range.start_date,
          to_date: range.end_date,
        })
        .catch(() => null),
    ])
      .then(([o, r, c, l, m, p]) => {
        if (!alive) return
        setOcc(o); setRev(r); setChan(c); setLost(l); setSeg(m); setPkgs(p)
      })
      .catch((e: unknown) => {
        if (!alive) return
        if (e instanceof ApiError && e.status === 401) {
          auth.clear()
          nav('/login', { replace: true })
          return
        }
        setErr(e instanceof Error ? t(e.message) : t('errors.generic'))
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [range, isMock, nav, t])

  const fmt = (n: number) => fmtMoney(n, i18n.language)

  async function openWinback(days = 60) {
    setWinbackOpen(true)
    setWinbackPreview(null)
    try {
      const p = await api.previewSegment('lapsed', { days_inactive: days })
      setWinbackPreview(p)
    } catch (e) {
      setErr(e instanceof Error ? t(e.message) : t('errors.generic'))
      setWinbackOpen(false)
    }
  }

  async function confirmWinback(messageText: string, days: number) {
    setWinbackBusy(true)
    try {
      const r = await api.sendWinback({ days_inactive: days, message_text: messageText })
      toast({
        title: t('marketing.sentToast', { count: r.recipient_count }),
        variant: 'success',
      })
      setWinbackOpen(false)
      api.marketingSegments().then(setSeg).catch(() => undefined)
    } catch (e) {
      setErr(e instanceof Error ? t(e.message) : t('errors.generic'))
    } finally {
      setWinbackBusy(false)
    }
  }

  return (
    <>
      <SubscriptionBanner />
      <div className="container max-w-[1180px] py-6 pb-24">
      {/* ---- Header ---- */}
      <header className="mb-7 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="grid size-9 place-items-center rounded-lg bg-primary text-primary-foreground">
            <PawPrint className="size-5" />
          </div>
          <span className="text-base font-semibold tracking-tight">{t('appName')}</span>
          {isMock && (
            <Badge variant="default" className="ml-1">
              {t('auth.mock')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <NativeSelect
            value={rangeKey}
            onChange={(v) => setRangeKey(v as RangeKey)}
            aria-label={t('range.label')}
            options={[
              { value: 'last7', label: t('range.last7') },
              { value: 'last30', label: t('range.last30') },
              { value: 'last90', label: t('range.last90') },
            ]}
          />
          <NativeSelect
            value={i18n.language}
            onChange={(v) => i18n.changeLanguage(v)}
            aria-label="locale"
            options={[
              { value: 'zh-Hant-TW', label: '中文' },
              { value: 'en', label: 'English' },
            ]}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav('/packages')}
            data-testid="nav-packages"
          >
            <PackageIcon className="size-4" />
            {t('packages.navLink')}
          </Button>
          {!isMock && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                auth.clear()
                nav('/login', { replace: true })
              }}
            >
              <LogOut className="size-4" />
              {t('auth.logout')}
            </Button>
          )}
        </div>
      </header>

      {err && (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
          {err}
        </div>
      )}

      {/* ---- Public booking page admin (Phase 6A) ---- */}
      <BookingPagePanel />

      {/* ---- Concierge policy KB (C1) ---- */}
      <PoliciesPanel />

      {/* ---- Lost-revenue hook — the sales-friendly headline ---- */}
      {lost && lost.lost_revenue > 0 && (
        <div className="mt-5 flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-warning">
          <Lightbulb className="size-5 shrink-0 translate-y-0.5" />
          <div className="flex-1">
            <div className="text-base font-semibold tnum">
              {t('lost.headline', { amount: fmt(lost.lost_revenue) })}
            </div>
            <div className="text-xs leading-relaxed text-warning/85">
              {t('lost.detail', {
                nights: lost.empty_nights,
                rate: Math.round(lost.avg_daily_rate),
              })}
              <span className="text-warning/70"> {t('lost.hint')}</span>
            </div>
          </div>
        </div>
      )}

      {/* ---- Stat tiles ---- */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        <StatTile
          icon={<LineChart className="size-4" />}
          label={t('stats.occupancy')}
          value={loading || !occ ? '—' : fmtPct(occ.avg_rate)}
          sub={occ ? `${occ.capacity} kennels · ${occ.empty_nights} empty nights` : ''}
        />
        <StatTile
          icon={<ShoppingBag className="size-4" />}
          label={t('stats.revenue')}
          value={loading || !rev ? '—' : fmt(rev.total_paid)}
          sub={rev ? `${t('stats.grossRevenue')}: ${fmt(rev.total_gross)}` : ''}
        />
        <StatTile
          icon={<Sparkles className="size-4" />}
          label={t('stats.bookings')}
          value={loading || !chan ? '—' : String(chan.total_count)}
          sub={t('stats.thisRange')}
        />
        <StatTile
          icon={<TrendingDown className="size-4" />}
          label={t('lost.label')}
          value={loading || !lost ? '—' : fmt(lost.lost_revenue)}
          sub={
            lost
              ? t('lost.subPattern', {
                  empty: lost.empty_nights,
                  total: lost.capacity * lost.nights,
                })
              : ''
          }
          tone="destructive"
        />
        <StatTile
          icon={<PackageIcon className="size-4" />}
          label={t('packages.dashboardTile.label')}
          value={loading || !pkgs ? '—' : String(pkgs.sold_count)}
          sub={
            pkgs
              ? `${fmt(pkgs.sold_revenue_minor / 100)} · ${t(
                  'packages.dashboardTile.expiringWithin7d',
                  { count: pkgs.expiring_within_7d },
                )}`
              : ''
          }
        />
      </div>

      {/* ---- Occupancy heatmap ---- */}
      <SectionLabel>{t('occupancy.title')}</SectionLabel>
      <Card>
        <CardContent className="p-5">
          {loading || !occ ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {t('loading')}
            </div>
          ) : occ.days.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t('occupancy.empty')}</div>
          ) : (
            <>
              <OccupancyHeatStrip days={occ.days} />
              <div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-muted-foreground">
                <span>{t('occupancy.legendEmpty')}</span>
                <span className="inline-flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className="inline-block size-2.5 rounded-[2px]"
                      style={{ background: `hsl(var(--heat-${i}))` }}
                    />
                  ))}
                </span>
                <span>{t('occupancy.legendFull')}</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ---- Revenue + Channel donut ---- */}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('revenue.title')}</CardTitle>
            <span className="text-[11px] text-muted-foreground">
              {t('revenue.paid')} · {t('revenue.gross')}
            </span>
          </CardHeader>
          <CardContent>
            {loading || !rev ? (
              <div className="py-8 text-center text-sm text-muted-foreground">{t('loading')}</div>
            ) : (
              <ServiceMixBars
                fmt={fmt}
                rows={revenueRows(rev, {
                  boarding: t('revenue.boarding'),
                  daycare: t('revenue.daycare'),
                  grooming: t('revenue.grooming'),
                })}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('channel.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading || !chan ? (
              <div className="py-8 text-center text-sm text-muted-foreground">{t('loading')}</div>
            ) : chan.total_count === 0 ? (
              <div className="text-sm text-muted-foreground">{t('channel.empty')}</div>
            ) : (
              <div className="flex flex-wrap items-center gap-4">
                <ChannelDonut channels={chan.channels} total={chan.total_count} />
                <div className="min-w-[160px] flex-1 space-y-1.5">
                  {chan.channels.map((c, i) => {
                    const pct = chan.total_count
                      ? Math.round((c.count / chan.total_count) * 100)
                      : 0
                    return (
                      <div
                        key={c.source}
                        className={`flex items-center justify-between py-1 text-sm ${
                          i > 0 ? 'border-t border-dashed border-border/60' : ''
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block size-2.5 rounded-[2px]"
                            style={{ background: getColor(c.source) }}
                          />
                          {t(`channel.${c.source}`, c.source)}
                        </span>
                        <span className="text-muted-foreground tnum">
                          {c.count} · {pct}%
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ---- Marketing command center ---- */}
      <MarketingSection segments={seg?.segments ?? null} onSendWinback={openWinback} />

      <SendWinbackDialog
        open={winbackOpen}
        preview={winbackPreview}
        busy={winbackBusy}
        onCancel={() => setWinbackOpen(false)}
        onSend={confirmWinback}
      />

      {/* ---- AI owner-growth assistant ---- */}
      <Assistant />
      </div>
    </>
  )
}

function StatTile({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode
  label: string
  value: string
  sub: string
  tone?: 'destructive'
}) {
  return (
    <div className="rounded-lg bg-secondary p-4">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span className="text-muted-foreground/70">{icon}</span>
        {label}
      </div>
      <div
        className={`mt-1 text-[28px] font-semibold leading-tight tracking-tight tnum ${
          tone === 'destructive' ? 'text-destructive' : ''
        }`}
      >
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 mt-6 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  )
}

function NativeSelect({
  value,
  onChange,
  options,
  ...rest
}: Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> & {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
      {...rest}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

const COLORS: Record<string, string> = {
  line: '#2f7a55',
  own_site: '#2563a8',
  google: '#b35d00',
  cherie: '#a8336d',
  staff: '#5b5e57',
  phone: '#1b1d1a',
  unknown: '#8d8f88',
}
function getColor(source: string): string {
  return COLORS[source] ?? COLORS.unknown
}
